/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

// Core imports
import React, { Fragment, useState, useRef, useCallback } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

// Component imports
import { Col, Row, Tabs } from 'antd';
import Permissioned from '../components/Permissioned';
import OrganizationSettingsForm from '../components/Forms/OrganizationSettingsForm';
import DocumentTitle from '../components/DocumentTitle';
import OrganizationLoginSettingsForm from '../components/Forms/OrganizationLoginSettingsForm';

// Misc imports
import { PERMISSIONS_GROUP_ADMIN, PERMISSIONS_GROUP_VIEWER } from '../constants/main';
import { useModalConfirm } from '../utils/hooks/useModalConfirm';
import useUserPermissions from '../utils/hooks/useUserPermissions';
import useBeforeUnload from '../utils/hooks/useBeforeUnload';

const TABS = {
  CONFIGURATION: 'configuration',
  LOGIN_SETTINGS: 'login'
};

const getActiveTabKey = (searchParams) => {
  if (searchParams.get('tab') === TABS.CONFIGURATION) {
    return TABS.CONFIGURATION;
  } else if (searchParams.get('tab') === TABS.LOGIN_SETTINGS) {
    return TABS.LOGIN_SETTINGS;
  }
  return TABS.CONFIGURATION;
};

const setActiveTabKey = (searchParams, tabKey) => {
  searchParams.set('tab', tabKey);
  return searchParams;
};

const OrganizationSettingsScreen = () => {
  const { orgId } = useParams();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const userPermissions = useUserPermissions(orgId, null);
  const currentOrgFormRef = useRef(null);

  const [hasPendingChanges, setHasPendingChanges] = useState(false);

  const { showModalConfirm } = useModalConfirm(hasPendingChanges);
  useBeforeUnload(hasPendingChanges, t('Are you sure you want to leave the page? Any unsaved changes will be lost.'));

  const handleTabClick = (tabKey) => {
    const params = setActiveTabKey(searchParams, tabKey);

    navigate({
      pathname: location.pathname,
      search: params.toString()
    }, { replace: true });
  };

  const setCurrentOrgFormRef = useCallback((ref) => {
    currentOrgFormRef.current = ref;
  }, []);

  const handleShowConfirm = (action) => {
    showModalConfirm({
      defaultAction: () => {
        action();
        setHasPendingChanges(false);
      },
      onConfirm: async () => {
        await currentOrgFormRef.current.submit();
        action();
        setHasPendingChanges(false);
      },
      okText: t('Save Changes'),
      cancelText: t('Quit without saving'),
      title: t('Save Changes?'),
      content: t('Your unsaved changes will be lost. Save changes before closing?')
    });
  };

  return (
    <Fragment>
      <DocumentTitle title={t('Workspace Settings')} />

      <Permissioned
        allowedPermissions={PERMISSIONS_GROUP_VIEWER}
        currentPermissions={userPermissions}
      >
        <Row gutter={24}>
          <Col span={24}>
            <Tabs
              activeKey={getActiveTabKey(searchParams)}
              onTabClick={(key) => {
                if (key !== searchParams.get('tab')) {
                  return handleShowConfirm(() => handleTabClick(key));
                }
              }}
              destroyInactiveTabPane
              animated={false}
              tabBarStyle={{ display: 'none' }}
              items={[
                {
                  key: TABS.CONFIGURATION,
                  label: t('Configuration'),
                  children: (
                    <Fragment>
                      <DocumentTitle title={t('Configuration')} />
                      <OrganizationSettingsForm
                        setFormRef={setCurrentOrgFormRef}
                        isUpdateMode={userPermissions.some(permission => PERMISSIONS_GROUP_ADMIN.includes(permission))}
                        setHasPendingChanges={setHasPendingChanges}
                        hasPendingChanges={hasPendingChanges}
                      />
                    </Fragment>
                  )
                },
                {
                  key: TABS.LOGIN_SETTINGS,
                  label: t('Login Settings'),
                  children: (
                    <Fragment>
                      <DocumentTitle title={t('Login Settings')} />
                      <OrganizationLoginSettingsForm
                        setFormRef={setCurrentOrgFormRef}
                        isUpdateMode={userPermissions.some(permission => PERMISSIONS_GROUP_ADMIN.includes(permission))}
                        setHasPendingChanges={setHasPendingChanges}
                      />
                    </Fragment>
                  )
                }
              ]}
            />
          </Col>
        </Row>
      </Permissioned>
    </Fragment>
  );
};

export default OrganizationSettingsScreen;
